<template>
    <BaseDrawer
        :open="open"
        :title="t('navigation.mobileMore.menu')"
        side="bottom"
        :reserve-mobile-nav-space="false"
        drawer-content-class="bg-nav-bg max-h-[75dvh] pb-[max(1.5rem,env(safe-area-inset-bottom))]"
        @update:open="!$event && emit('close')">
        <DefineItemContent v-slot="{ item }">
            <i v-if="'icon' in item && item.icon" class="bi text-lg" :class="[item.icon, isItemActive(item) ? 'text-nav-menu-active-text' : 'text-nav-icon']" aria-hidden="true" />
            <span v-else-if="'iconComponent' in item && item.iconComponent" class="w-4.5 h-4.5"><component :is="item.iconComponent" /></span>
            <span :class="isItemActive(item) ? 'body-semibold text-nav-menu-active-text' : 'body text-ink'">
                {{ item.title }}
            </span>
            <NotificationMarble v-if="hasNotification(item)" :size="2" class="ml-auto" />
        </DefineItemContent>
        <nav class="sheet-list">
            <template v-for="item in items" :key="item.id">
                <ValencerInstanceSwitcher v-if="item.id === 'logout' && instanceSwitcher" :show-labels="true" presentation="modal" />
                <BaseLink
                    v-if="item.type === 'link'"
                    v-bind="itemAttrs(item)"
                    :href="item.url"
                    class="sheet-item"
                    :class="{ 'sheet-item-active': isItemActive(item) }"
                    @click="handleItemClick(item)">
                    <ItemContent :item="item" />
                </BaseLink>
                <!-- New-tab and mailto rows the browser must open, so a plain anchor rather than BaseLink. -->
                <!-- eslint-disable-next-line vuejs-accessibility/anchor-has-content -- the title is rendered by ItemContent, which the rule cannot see -->
                <a
                    v-else-if="item.type === 'url'"
                    v-bind="itemAttrs(item)"
                    :href="item.url"
                    :target="item.target"
                    :rel="item.target ? 'noopener noreferrer' : undefined"
                    class="sheet-item"
                    :class="{ 'sheet-item-active': isItemActive(item) }"
                    @click="handleItemClick(item)">
                    <ItemContent :item="item" />
                </a>
                <BaseButton v-else v-bind="itemAttrs(item)" theme="ghost" class="sheet-item whitespace-normal active:scale-100" @click="handleItemClick(item)">
                    <ItemContent :item="item" />
                </BaseButton>
            </template>
        </nav>

        <form ref="logoutForm" method="POST" :action="logoutUrl" class="hidden">
            <input type="hidden" name="csrfmiddlewaretoken" :value="getCookie('csrftoken')" />
        </form>
    </BaseDrawer>
</template>

<script setup lang="ts">
import { getCookie } from "/js/utils.js";
import { usePage } from "@inertiajs/vue3";
import { createReusableTemplate } from "@vueuse/core";
import { type GlobalProperties, type OrganizationDetails, panesWithNotificationKey, type StaffLink, type UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDrawer from "~vue/components/BaseDrawer.vue";
import NotificationMarble from "~vue/components/NotificationMarble.vue";
import { useNadiaTutorialAnalytics } from "~vue/composables/useNadiaTutorialAnalytics";
import AlertCircle from "~vue/icons/AlertCircle.vue";
import Logout from "~vue/icons/Logout.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { isInIFrame } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { discardBrowserHistory } from "~vue/utils/browserHistory";
import { shutdownIntercomMessengerEverywhere } from "~vue/utils/intercom";
import { logUserInteraction } from "~vue/utils/logUtils";
import type { Component, ComputedRef } from "vue";
import { computed, inject, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS, MainMenuPane, privacyRowLabel } from "./menuPanes";
import ValencerInstanceSwitcher from "./ValencerInstanceSwitcher.vue";

const { t } = useI18n();

const page = usePage<{ staff_links?: StaffLink[] }>();
const panesWithNotifications = inject(panesWithNotificationKey, ref([]));
function hasNotification(item: SheetItem): boolean {
    return (item.id === MainMenuPane.GUIDED_SETUP || item.id === MainMenuPane.CONNECTIONS) && panesWithNotifications.value.includes(item.id);
}

interface SheetLinkItem {
    id: string;
    title: string;
    type: "link";
    icon: string;
    url: string;
}

interface SheetUrlItem {
    id: string;
    title: string;
    type: "url";
    url: string;
    icon?: string;
    iconComponent?: Component;
    target?: string;
}

interface SheetActionItem {
    id: string;
    title: string;
    type: "action";
    icon?: string;
    iconComponent?: Component;
    action: () => void;
}

type SheetItem = SheetLinkItem | SheetUrlItem | SheetActionItem;

const [DefineItemContent, ItemContent] = createReusableTemplate<{ item: SheetItem }>();

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: []; openPrivacy: [] }>();

const globalProperties = inject<GlobalProperties>("globalProperties")!;
const userDetails = inject<ComputedRef<UserDetails>>("user_details")!;
const { logLinkViewed, logTutorialEvent } = useNadiaTutorialAnalytics();

watch(
    () => props.open && userDetails.value?.nadia_tutorial_enabled,
    (visible) => {
        if (visible) {
            logLinkViewed("mobile_more_sheet", true);
        }
    },
    { immediate: true },
);

const instanceSwitcher = computed(() => userDetails.value?.valencer_instance_switcher ?? null);
const staffItems = computed<SheetItem[]>(() =>
    (page.props.staff_links ?? []).map((link) => ({
        id: `staff-${link.url}`,
        title: link.title,
        type: link.is_inertia ? "link" : "url",
        icon: link.icon,
        url: link.url,
    })),
);

const disabledPanes = inject("mobileDisabledPanes", ref<string[]>([]));
const organizationDetails = inject<ComputedRef<OrganizationDetails>>("organization_details")!;
const alignHost = inject<ComputedRef<string>>("align_host")!;
const logoutForm = ref<HTMLFormElement | null>(null);
const logoutUrl = isInIFrame() ? "/accounts/logout/?next=/microsoft-teams/login/" : "/accounts/logout/";

function handleLogout() {
    discardBrowserHistory();
    shutdownIntercomMessengerEverywhere();
    logoutForm.value?.submit();
}

function itemAttrs(item: SheetItem) {
    const base: Record<string, unknown> = { "data-testid": item.id };
    if (hasNotification(item)) {
        base["aria-label"] = t("navigation.a11y.paneNew", { title: item.title });
    }
    return base;
}

function handleItemClick(item: SheetItem) {
    if (item.id === "team-tools") {
        // English, for Amplitude: event properties must not change with the user's language.
        logUserInteraction(AmplitudeEventNames.MAIN_MENU_ITEM_CLICKED, { id: item.id, title: "Valence Team Tools" });
    }
    if (item.id === MainMenuPane.PERFORMANCE_MANAGEMENT) {
        logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_ENTRY_CLICKED, { entry_source: "side_nav" });
    }
    if (item.id === "nadia-tutorial") {
        logTutorialEvent(AmplitudeEventNames.NADIA_TUTORIAL_LINK_CLICKED, { entry_source: "mobile_more_sheet" });
    }
    if (item.type === "action") {
        item.action();
    } else {
        emit("close");
    }
}

function isItemActive(item: SheetItem): boolean {
    if (item.type === "action") {
        return false;
    }
    return page.url.startsWith(item.url);
}

const items = computed<SheetItem[]>(() => {
    const perfReviewLabel = organizationDetails?.value?.perf_review_custom_copy?.page_tab_label || t("navigation.legacyPanes.performanceManagement");

    const allItems: SheetItem[] = [
        { id: MainMenuPane.GUIDED_SETUP, title: t("navigation.panes.coachingSetup"), type: "link", icon: "bi-stars", url: "/onboarding/calendar-first/setup/" },
        { id: "profile", title: t("navigation.panes.profile"), type: "link", icon: "bi-person-circle", url: "/accounts/user-profile" },
        {
            id: MainMenuPane.COLLABORATION_PROFILE,
            title: t("navigation.legacyPanes.collaborationProfile"),
            type: "link",
            icon: COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS,
            url: "/accounts/collaboration-profile",
        },
        { id: MainMenuPane.JOURNEYS, title: t("navigation.panes.journeys"), type: "link", icon: "bi-compass", url: "/journeys/" },
        {
            id: MainMenuPane.DEVELOPMENT_JOURNEYS,
            title: t("navigation.legacyPanes.developmentJourneys"),
            type: "link",
            icon: "bi-signpost-split",
            url: "/development-journeys/",
        },
        {
            id: MainMenuPane.MY_DEVELOPMENT,
            title: t("myDevelopment.title"),
            type: "link",
            icon: "bi-signpost-split",
            url: "/my-development/",
        },
        { id: MainMenuPane.CONNECTIONS, title: t("navigation.panes.people"), type: "link", icon: "bi-people", url: "/people/" },
        { id: MainMenuPane.PERFORMANCE_MANAGEMENT, title: perfReviewLabel, type: "link", icon: "bi-graph-up", url: "/performance_management" },
        { id: MainMenuPane.REFLECTIONS, title: t("navigation.panes.reflections"), type: "link", icon: "bi-journal-text", url: "/reflections" },
        ...(userDetails?.value?.is_customer_demo_accounts_enabled
            ? ([{ id: "demo-accounts", title: t("navigation.tools.demoAccounts"), type: "link", icon: "bi-person-badge", url: "/demo-accounts/" }] satisfies SheetItem[])
            : []),
        ...(userDetails?.value?.can_see_client_analytics
            ? ([{ id: "analytics", title: t("navigation.tools.analytics"), type: "link", icon: "bi-bar-chart", url: "/client-analytics/" }] satisfies SheetItem[])
            : []),
        ...(organizationDetails?.value?.team_tools_link_enabled
            ? ([
                  { id: "team-tools", title: t("navigation.tools.teamTools"), type: "url", url: alignHost.value, icon: "bi-box-arrow-up-right", target: "_blank" },
              ] satisfies SheetItem[])
            : []),
        { id: "preferences", title: t("navigation.account.preferences"), type: "link", icon: "bi-sliders", url: "/accounts/user-preferences" },
        {
            id: "privacy",
            title: privacyRowLabel(organizationDetails?.value?.privacy_link_label),
            type: "action",
            icon: "bi-shield-lock",
            action: () => {
                emit("close");
                emit("openPrivacy");
            },
        },
        ...(userDetails?.value?.nadia_tutorial_enabled
            ? ([{ id: "nadia-tutorial", title: t("navigation.tools.nadiaTutorial"), type: "link", icon: "bi-question-circle", url: "/nadia-tutorial/" }] satisfies SheetItem[])
            : []),
        {
            id: "feedback",
            title: t("navigation.legacyAccount.reportIssue"),
            type: "url",
            url: isInIFrame() ? "/microsoft-teams/report-issue/" : globalProperties.$feedbackMailto,
            iconComponent: AlertCircle,
        },
        ...staffItems.value,
        { id: "logout", title: t("navigation.legacyAccount.logout"), type: "action", iconComponent: Logout, action: handleLogout },
    ];

    return allItems.filter((item) => {
        if (item.type === "link" && Object.values(MainMenuPane).includes(item.id as MainMenuPane)) {
            return !disabledPanes.value.includes(item.id);
        }
        return true;
    });
});
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    /* The pressed state keeps the sheet's hover tint, so a BaseButton row never flashes the ghost button's active fill. */
    .sheet-item {
        @apply flex items-center gap-4 px-3 py-2.5 rounded-xl hover:bg-nav-sheet-hover-bg active:bg-nav-sheet-hover-bg transition-colors w-full text-left;
    }

    .sheet-item-active {
        @apply bg-nav-menu-active-bg;
    }

    .sheet-list {
        @apply px-6 py-2;
    }
}
</style>
